import { getActiveMcpServers, isStandalone, PPAI_STANDALONE_AUTH_REQUIRED } from "../config/ppAiConfig"
import PPUtils from "./PPUtils"
import { getStandaloneToken, clearStandaloneToken } from "./ppAiStandaloneAuth"

const MCP_REQUEST_TIMEOUT_MS = 10 * 60 * 1000

/**
 * Session-ID: Kommt aus der ersten Response (initialize). Für Folgerequests wird
 * "mcp-session-id" im Request-Header gesendet.
 * Damit der Browser den Response-Header lesen kann, muss der MCP-Server bei CORS
 * senden: Access-Control-Expose-Headers: mcp-session-id
 * Alternativ: Session-ID im JSON-Body der Initialize-Response (result.sessionId).
 */

const sessionByServerId = new Map()
let toolServerMap = new Map()

function getCurrentMcpServer() {
    const active = getActiveMcpServers()
    return active.length ? active[0] : null
}

function getServerAuthHeader(server) {
    if (server.useStandaloneIpToken && isStandalone()) {
        const token = getStandaloneToken()
        return token ? `Bearer ${token}` : ""
    }
    if (server.useLoginMcpToken) {
        const token = PPUtils.getLogin()?.mcpServerToken
        return token ? `Bearer ${token}` : ""
    }
    if (server.bearerToken) return `Bearer ${server.bearerToken}`
    if (server.useLoginToken) {
        const token = PPUtils.getLogin()?.token
        return token ? `Bearer ${token}` : ""
    }
    return ""
}

function attachAbortSignal(externalSignal, controller) {
    if (!externalSignal) return () => {}
    if (externalSignal.aborted) {
        controller.abort()
        return () => {}
    }
    const onAbort = () => controller.abort()
    externalSignal.addEventListener("abort", onAbort, { once: true })
    return () => externalSignal.removeEventListener("abort", onAbort)
}

async function mcpSendNotification(server, method, params = {}, options = {}) {
    if (!server) throw new Error("Kein MCP-Server konfiguriert")
    const sessionId = sessionByServerId.get(server.id)
    if (!sessionId) return
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), MCP_REQUEST_TIMEOUT_MS)
    const detachAbort = attachAbortSignal(options?.signal, controller)
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
        Authorization: getServerAuthHeader(server),
        "mcp-session-id": sessionId
    }
    if (!headers.Authorization) delete headers.Authorization
    let res
    try {
        res = await fetch(server.url, {
            method: "POST",
            headers,
            body: JSON.stringify({
                jsonrpc: "2.0",
                method,
                params
            }),
            signal: controller.signal
        })
    } finally {
        detachAbort()
        clearTimeout(timeoutId)
    }
    if (!res.ok) {
        if (res.status === 401 && isStandalone()) {
            clearStandaloneToken()
            const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
            e.code = PPAI_STANDALONE_AUTH_REQUIRED
            throw e
        }
        const text = await res.text()
        throw new Error(`MCP ${method} failed: ${res.status} ${text}`)
    }
    const sessionFromResponse =
        res.headers.get("mcp-session-id") ?? res.headers.get("Mcp-Session-Id")
    if (sessionFromResponse) sessionByServerId.set(server.id, sessionFromResponse)
    const contentType = res.headers.get("Content-Type") || ""
    if (contentType.includes("application/json")) {
        const raw = await res.text()
        let data
        try {
            data = JSON.parse(raw)
        } catch {
            if (raw.trimStart().startsWith("<")) {
                throw new Error("MCP-Server antwortet mit HTML statt JSON. Backend/URL prüfen.")
            }
            throw new Error(`MCP ${method}: Ungültige JSON-Antwort`)
        }
        if (isStandalone() && (data?.status === 401 || (data?.error === "Unauthorized" && data?.status === 401))) {
            clearStandaloneToken()
            const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
            e.code = PPAI_STANDALONE_AUTH_REQUIRED
            throw e
        }
        if (data.error) throw new Error(data.error.message || JSON.stringify(data.error))
    }
}

async function mcpRequest(server, method, params = {}, id = 1, options = {}) {
    if (!server) throw new Error("Kein MCP-Server konfiguriert")
    const auth = getServerAuthHeader(server)
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
        ...(auth ? { Authorization: auth } : {})
    }
    const sessionId = sessionByServerId.get(server.id)
    if (sessionId) {
        headers["mcp-session-id"] = sessionId
    }
    const startTime = Date.now()
    let res
    let shouldRetry = true
    while (shouldRetry) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), MCP_REQUEST_TIMEOUT_MS)
        const detachAbort = attachAbortSignal(options?.signal, controller)
        try {
            res = await fetch(server.url, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    jsonrpc: "2.0",
                    id,
                    method,
                    params
                }),
                signal: controller.signal
            })
            clearTimeout(timeoutId)
            detachAbort()
            shouldRetry = false
        } catch (err) {
            clearTimeout(timeoutId)
            detachAbort()
            if (err.name === "AbortError") {
                if (options?.signal?.aborted) throw err
                throw new Error(`MCP_TIMEOUT:${method}`)
            }
            if (err.message?.includes("NetworkError") || err.message?.includes("Failed to fetch")) {
                if (Date.now() - startTime < MCP_REQUEST_TIMEOUT_MS) {
                    await new Promise((resolve) => setTimeout(resolve, 2000))
                    continue
                }
                throw new Error("MCP_NETWORK_ERROR")
            }
            throw err
        }
    }
    const sessionFromResponse =
        res.headers.get("mcp-session-id") ?? res.headers.get("Mcp-Session-Id")
    if (sessionFromResponse) {
        sessionByServerId.set(server.id, sessionFromResponse)
    }
    if (!res.ok) {
        const text = await res.text()
        throw new Error(`MCP ${method} failed: ${res.status} ${text}`)
    }
    const contentType = res.headers.get("Content-Type") || ""
    let data
    if (contentType.includes("text/event-stream")) {
        data = await readFirstSseJson(res)
    } else if (contentType.includes("application/json")) {
        const raw = await res.text()
        try {
            data = JSON.parse(raw)
        } catch (e) {
            if (raw.trimStart().startsWith("<")) {
                throw new Error("MCP-Server antwortet mit HTML statt JSON. Backend/URL prüfen.")
            }
            data = null
        }
    } else {
        const raw = await res.text()
        if (raw) {
            data = parseFirstSseJsonFromText(raw)
            if (data == null) {
                try {
                    data = JSON.parse(raw)
                } catch {
                    data = null
                }
            }
        }
    }
    if (data?.error) {
        if (isStandalone() && (data.status === 401 || data.error === "Unauthorized")) {
            clearStandaloneToken()
            const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
            e.code = PPAI_STANDALONE_AUTH_REQUIRED
            throw e
        }
        throw new Error(data.error.message || JSON.stringify(data.error))
    }
    if (isStandalone() && (data?.status === 401 || (data?.error === "Unauthorized" && data?.status === 401))) {
        clearStandaloneToken()
        const e = new Error(PPAI_STANDALONE_AUTH_REQUIRED)
        e.code = PPAI_STANDALONE_AUTH_REQUIRED
        throw e
    }
    const fromBody =
        data?.result?.sessionId ??
        data?.result?.session_id ??
        data?.sessionId ??
        data?.session_id
    if (fromBody) sessionByServerId.set(server.id, fromBody)
    if (!sessionByServerId.get(server.id) && method === "initialize" && data?.result) {
        const r = data.result
        sessionByServerId.set(server.id, r.sessionId ?? r.session_id ?? r.sessionID)
    }
    if (data?.result !== undefined && data?.result !== null) return data.result
    if (method === "tools/list" && data?.tools !== undefined) {
        const t = data.tools
        return {
            tools: Array.isArray(t) ? t : (t && typeof t === "object" ? Object.values(t) : [])
        }
    }
    return undefined
}

async function readFirstSseJson(res) {
    if (!res.body) return null
    const reader = res.body.getReader()
    const decoder = new TextDecoder("utf-8")
    let fullText = ""
    try {
        while (true) {
            const { value, done } = await reader.read()
            if (done) break
            fullText += decoder.decode(value, { stream: true })
        }
    } finally {
        reader.releaseLock()
    }
    return parseFirstSseJsonFromText(fullText)
}

function parseFirstSseJsonFromText(text) {
    if (!text || typeof text !== "string") return null
    const lines = text.split(/\r?\n/)
    let afterEmptyData = false
    for (const line of lines) {
        const trimmed = line.trim()
        if (afterEmptyData) {
            afterEmptyData = false
            if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
                try {
                    return JSON.parse(trimmed)
                } catch {
                    // skip
                }
            }
            continue
        }
        if (!trimmed.startsWith("data:")) continue
        const payload = trimmed.replace(/^data:\s*/, "").trim()
        if (payload === "[DONE]") continue
        if (!payload) {
            afterEmptyData = true
            continue
        }
        let jsonStr = payload
        if ((payload.startsWith("{") || payload.startsWith("[")) && (payload.includes("}") || payload.includes("]"))) {
            const end = findJsonEnd(payload)
            if (end > 0) jsonStr = payload.slice(0, end)
        }
        try {
            return JSON.parse(jsonStr)
        } catch {
            // skip truncated line
        }
    }
    const dataIdx = text.indexOf("data:")
    if (dataIdx >= 0) {
        const rest = text.slice(dataIdx + 5).replace(/^\s+/, "")
        const end = findJsonEnd(rest)
        if (end > 0) {
            try {
                return JSON.parse(rest.slice(0, end))
            } catch {
                // ignore
            }
        }
    }
    return null
}

function findJsonEnd(str) {
    if (!str || typeof str !== "string") return -1
    const start = str.search(/\{|\[/)
    if (start < 0) return -1
    const stack = [str[start]]
    for (let i = start + 1; i < str.length; i++) {
        const c = str[i]
        if (c === "\\" && i + 1 < str.length) {
            i++
            continue
        }
        if (c === "{") stack.push("{")
        else if (c === "[") stack.push("[")
        else if (c === "}" || c === "]") {
            const expected = c === "}" ? "{" : "["
            if (stack[stack.length - 1] !== expected) return -1
            stack.pop()
            if (stack.length === 0) return i + 1
        }
    }
    return -1
}

export async function mcpInitializeAll(options = {}) {
    const servers = getActiveMcpServers()
    for (const server of servers) {
        const result = await mcpRequest(server, "initialize", {
            protocolVersion: "2024-11-05",
            capabilities: {},
            clientInfo: { name: "pp-frontend-ai", version: "1.0.0" }
        }, 1, options)
        if (result?.sessionId) sessionByServerId.set(server.id, result.sessionId)
        await mcpSendNotification(server, "notifications/initialized", {}, options)
    }
}

function normalizeToolsList(raw) {
    if (raw == null) return []
    if (Array.isArray(raw)) return raw
    if (typeof raw === "object") return Object.values(raw)
    return []
}

export async function mcpToolsListAll(options = {}) {
    const servers = getActiveMcpServers()
    const hasCustomerMcp = servers.some((s) => s.name === "PP-Customer-Server-MCP")
    toolServerMap = new Map()
    const allTools = []
    for (const server of servers) {
        let result
        try {
            result = await mcpRequest(server, "tools/list", {}, 2, options)
        } catch (err) {
            continue
        }
        const tools = normalizeToolsList(result?.tools)
        for (const tool of tools) {
            const name = tool?.name ?? tool?.function?.name
            if (!name) continue
            if (hasCustomerMcp && server.id === "pp-mcp-remote" && name === "offersMarkdownToCsv") continue
            if (!toolServerMap.has(name)) toolServerMap.set(name, server)
            allTools.push(tool)
        }
    }
    return { tools: allTools }
}

export async function mcpToolsCall(name, args, options = {}) {
    const server = toolServerMap.get(name) || getCurrentMcpServer()
    return mcpRequest(server, "tools/call", { name, arguments: args || {} }, 3, options)
}
